import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import { forwardRef } from "react";

import { ApiError, detailErrors } from "../lib/api";

/** Small, project-specific primitives on brand tokens (no hardcoded colors, Article VII). */

type Variant = "primary" | "secondary" | "danger" | "ghost";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-brand-primary text-white hover:opacity-90",
  secondary: "border border-brand-line bg-white text-brand-text hover:bg-brand-tint",
  danger: "bg-red-700 text-white hover:bg-red-800",
  ghost: "text-brand-primary hover:bg-brand-tint",
};

export function Button({
  variant = "primary",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${VARIANTS[variant]} ${className}`}
      {...props}
    />
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = "", ...props }, ref) {
    return (
      <input
        ref={ref}
        className={`w-full rounded-md border border-brand-line bg-white px-2.5 py-1.5 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-tint ${className}`}
        {...props}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className = "", ...props }, ref) {
    return (
      <select
        ref={ref}
        className={`w-full rounded-md border border-brand-line bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-primary ${className}`}
        {...props}
      />
    );
  },
);

export function Field({
  label,
  error,
  children,
  hint,
}: {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-brand-text">{label}</span>
      {children}
      {hint && !error ? <span className="text-xs text-brand-muted">{hint}</span> : null}
      {error ? <span className="text-xs text-red-700">{error}</span> : null}
    </label>
  );
}

export function Card({
  title,
  actions,
  children,
  className = "",
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-lg border border-brand-line bg-white shadow-sm ${className}`}>
      {title || actions ? (
        <header className="flex items-center justify-between gap-3 border-b border-brand-line px-4 py-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function PageTitle({
  children,
  subtitle,
  actions,
}: {
  children: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-brand-text">{children}</h1>
        {subtitle ? <p className="text-sm text-brand-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Shows an API error the way support needs it: the Spanish message and the request id. */
export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : "Ocurrió un error inesperado.";
  const list = detailErrors(error);
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  return (
    <div
      role="alert"
      className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
    >
      <p className="font-medium">{message}</p>
      {list.length > 1 ? (
        <ul className="mt-1 list-disc pl-5">
          {list.slice(1).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {requestId ? (
        <p className="mt-1 text-xs text-red-600">Código para soporte: {requestId}</p>
      ) : null}
    </div>
  );
}

export function Notice({
  tone = "info",
  children,
}: {
  tone?: "info" | "warning" | "success";
  children: ReactNode;
}) {
  const tones = {
    info: "border-brand-line bg-brand-tint text-brand-text",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  };
  return <div className={`rounded-md border px-3 py-2 text-sm ${tones[tone]}`}>{children}</div>;
}

export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "brand" | "warning" | "danger" | "success";
}) {
  const tones = {
    neutral: "bg-gray-100 text-gray-700",
    brand: "bg-brand-tint text-brand-primary",
    warning: "bg-amber-100 text-amber-800",
    danger: "bg-red-100 text-red-800",
    success: "bg-emerald-100 text-emerald-800",
  };
  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

export const tableClass = "w-full border-collapse text-sm";
export const thClass =
  "border-b border-brand-line px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-brand-muted";
export const tdClass = "border-b border-brand-line px-2 py-1.5 align-top";
export const numClass = "text-right tabular whitespace-nowrap";

/** What a page shows to a role that may not see it; the API refuses too, this keeps the screen honest. */
export function NoAccess() {
  return (
    <Notice tone="warning">
      Esta sección es solo del Dueño del estudio. Si la necesitás, pedile que te dé acceso.
    </Notice>
  );
}
